import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";

/** Extra gate on top of each hook's own "has an id" check. */
interface ResourceOptions {
  enabled?: boolean;
}

/** Tenant links (owner / shared) of an organization. */
export function useOrgTenants(orgId: string | null | undefined, { enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.organizationTenant.byOrg(orgId ?? ""),
    queryFn: () => api.getOrgTenants(orgId!),
    enabled: !!orgId && enabled,
  });
}

/**
 * Tenants the user can access. Without an `orgId` argument: all of them;
 * with one: that organization's (disabled while it is null).
 */
export function useTenants(orgId?: string | null, { enabled = true }: ResourceOptions = {}) {
  const all = orgId === undefined;
  return useQuery({
    queryKey: all ? keys.tenant.list() : keys.tenant.byOrg(orgId ?? ""),
    queryFn: () => api.getTenants(all ? undefined : { organization_id: orgId! }),
    enabled: (all || !!orgId) && enabled,
  });
}

export function useOrgMembers(orgId: string | null | undefined, { enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.organizationMembership.byOrg(orgId ?? ""),
    queryFn: () => api.getOrgMembers(orgId!),
    enabled: !!orgId && enabled,
  });
}

export function useOrgInvitations(orgId: string | null | undefined, { enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.orgInvitation.byOrg(orgId ?? ""),
    queryFn: () => api.getOrgInvitations(orgId!),
    enabled: !!orgId && enabled,
  });
}

export function useUsers({ enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.user.list(),
    queryFn: api.getUsers,
    enabled,
  });
}

/** Agent data source catalog; with a tenant, its availability for that tenant. */
export function useAgentDataSources(tenantId: string | null = null, { enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.agentDefinition.dataSources(tenantId),
    queryFn: () => api.getAgentDataSources(tenantId),
    enabled,
  });
}

/** Signal data types; with a tenant the list carries its choices and availability. */
export function useWorkflowDataTypes(tenantId: string | null = null, { enabled = true }: ResourceOptions = {}) {
  return useQuery({
    queryKey: keys.workflowDataType.list(tenantId),
    queryFn: () => api.getWorkflowDataTypes(tenantId),
    enabled,
  });
}
